import type { ScrollAxisName } from "./scroll-box-axes"
import type { ScrollBoxIdentity } from "./scroll-box-identity"
import type { ScrollBoxVerdict } from "./scroll-box-verdict"

export const SCROLL_BOX_FINDING_KINDS = [
  "too-small",
  "cut-off-content",
  "outside-window",
  "hidden-end",
  "cut-popup",
  "unused-room",
] as const

export type ScrollBoxFindingKind = (typeof SCROLL_BOX_FINDING_KINDS)[number]

export type ScrollEdge = "start" | "end"

export type OutsideWindowReason = "fixed-layer" | "page-clips" | "before-page-start"

export type HiddenEndCause = "overlay" | "window-or-clip"

interface ScrollBoxFindingBase {
  identity: ScrollBoxIdentity
  boxWidthPx: number
  boxHeightPx: number
}

export interface TooSmallScrollBoxFinding extends ScrollBoxFindingBase {
  kind: "too-small"
  verdict: ScrollBoxVerdict
}

export interface CutOffContentFinding extends ScrollBoxFindingBase {
  kind: "cut-off-content"
  axis: ScrollAxisName
  cutOffPx: number
}

export interface OutsideWindowFinding extends ScrollBoxFindingBase {
  kind: "outside-window"
  axis: ScrollAxisName
  edge: ScrollEdge
  reason: OutsideWindowReason
  outsidePx: number
}

export interface HiddenEndFinding extends ScrollBoxFindingBase {
  kind: "hidden-end"
  axis: ScrollAxisName
  edge: ScrollEdge
  cause: HiddenEndCause
  hiddenPx: number
}

export interface CutPopupFinding extends ScrollBoxFindingBase {
  kind: "cut-popup"
  axis: ScrollAxisName
  popupKey: string
  cutOffPx: number
}

export interface UnusedRoomFinding extends ScrollBoxFindingBase {
  kind: "unused-room"
  axis: ScrollAxisName
  frameKey: string
  unusedRoomPx: number
  medianUnitPx: number
}

export type ScrollBoxFinding =
  | TooSmallScrollBoxFinding
  | CutOffContentFinding
  | OutsideWindowFinding
  | HiddenEndFinding
  | CutPopupFinding
  | UnusedRoomFinding

export interface DetectedFinding {
  element: Element
  finding: ScrollBoxFinding
}

export const SCROLL_BOX_FINDING_LABELS: Record<ScrollBoxFindingKind, string> = {
  "too-small": "Too-small scroll box",
  "cut-off-content": "Cut-off content",
  "outside-window": "Content outside the window",
  "hidden-end": "Hidden end of a scroll box",
  "cut-popup": "Popup cut by its container",
  "unused-room": "Scroll box leaves room unused",
}

export function findingAxes(finding: ScrollBoxFinding): ScrollAxisName[] {
  if (finding.kind === "too-small") {
    return finding.verdict.overflowingAxes.filter((axisVerdict) => axisVerdict.isTooSmall).map((axisVerdict) => axisVerdict.axis)
  }
  return [finding.axis]
}
